import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormHelperText, FormLabel } from "@mui/material";
import type { FileUploadProps } from "@components/FileUpload/FileUpload.types";
import FileUploadItem from "@components/FileUpload/FileUploadItem";
import type { UploadEntry } from "@components/FileUpload/uploadStore";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import { t } from "i18next";
import { useDropzone, type FileRejection } from "react-dropzone";

const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];

const normalizeExtension = (ext: string) => {
  const withDot = ext.startsWith(".") ? ext : "." + ext;
  return withDot.toLowerCase();
};

const mergeUnique = (list: string[]) => Array.from(new Set(list));

const buildTestId = (base: string | undefined, suffix: string) =>
  base ? base + "-" + suffix : undefined;

const FileUpload: React.FC<FileUploadProps> = ({
  label,
  labelId,
  onUpload,
  onRemoveFile,
  onReject,
  testId,
  conditions,
  name,
  maxFiles,
  allowedExtensions,
  disabled,
  allowReplaceExisting = false,
  uploadStoreKey,
  files,
  setFiles,
  required,
  uploadDescription,
  isSideDrawer,
  validateFiles,
  onValidationResult,
  validateDrop,
  isError,
  errorMessage,
}) => {
  void onRemoveFile;
  const normalizedAllowedExtensions = useMemo(
    () => mergeUnique(allowedExtensions.map(normalizeExtension)),
    [allowedExtensions],
  );
  const allowedExtensionsDisplay = useMemo(
    () => normalizedAllowedExtensions.join(", "),
    [normalizedAllowedExtensions],
  );

  const storedFiles = useUploadStore((state) =>
    uploadStoreKey
      ? (state.filesByKey[uploadStoreKey] ?? EMPTY_UPLOAD_ENTRIES)
      : name
        ? (state.filesByKey[name] ?? EMPTY_UPLOAD_ENTRIES)
        : EMPTY_UPLOAD_ENTRIES,
  );
  const currentFiles = files && files.length ? files : storedFiles;

  const maxFilesLimit = Math.max(1, maxFiles);
  const isSingleFileMode = maxFilesLimit === 1;

  const isAllowedType = useCallback(
    (file: File) => {
      const extension = normalizeExtension(
        file.name.substring(file.name.lastIndexOf(".")) || file.name,
      );
      return normalizedAllowedExtensions.includes(extension);
    },
    [normalizedAllowedExtensions],
  );

  const isUnderMaxSize = useCallback(
    (file: File) => {
      const maxSize = conditions?.maxSizeBytes;
      return maxSize ? file.size <= maxSize : true;
    },
    [conditions],
  );

  const [fileSizeError, setFileSizeError] = useState(false);
  const [fileTypeError, setFileTypeError] = useState(false);

  /**
   * General guideline (DFT-0088 addendum, 2026-09-01): once a slot's `maxFiles` is already
   * reached, the drag-and-drop area (and its file-picker trigger) must be disabled, re-enabling
   * only once a file is removed. This line already enforces that automatically for any consumer
   * that lets `FileUpload` own its own file list via `files`/`setFiles` (the overwhelming
   * majority of consumers) - `currentFiles.length >= maxFilesLimit` naturally becomes true the
   * moment the slot fills and false again the moment a file is removed.
   *
   * It does NOT, however, see a document that a consumer tracks as "already attached" through
   * some OTHER, externally-owned prop/value instead of this component's own `files` list (e.g. a
   * prefilled document representing something that already exists server-side, before the vendor
   * has touched this control at all in the current session). Such a consumer must compute and
   * pass its own equivalent `disabled` whenever that external value is non-empty, or the dropzone
   * will incorrectly stay interactive despite the slot already being "full" from the user's point
   * of view. See `BayHireCreateAttachmentUpload.tsx`'s `disabled={... || (!!requestId &&
   * !!value)}` for a worked example (Update/Resubmit disable once their externally-prefilled
   * `value` is attached; Create/Renew never prefill one, so they are unaffected).
   */
  const isAtCapacity = currentFiles.length >= maxFilesLimit;
  const isDisabled = disabled || (isAtCapacity && !(allowReplaceExisting && isSingleFileMode));

  const syncFilesToStore = useCallback(
    (incoming: UploadEntry[], snapshot: UploadEntry[]) => {
      const storeKey = uploadStoreKey ?? name;
      if (!storeKey) return;
      const store = useUploadStore.getState();

      if (isSingleFileMode) {
        store.setFilesForKey(storeKey, snapshot, { mode: "replace", maxFiles: 1 });
        return;
      }

      store.setFilesForKey(storeKey, incoming, { mode: "merge", maxFiles: maxFilesLimit });
    },
    [isSingleFileMode, maxFilesLimit, name, uploadStoreKey],
  );

  const processFiles = useCallback(
    async (newFiles: File[]) => {
      const filtered = newFiles.filter((file) => isUnderMaxSize(file) && isAllowedType(file));
      const rejectedFiles = newFiles.filter((file) => !isUnderMaxSize(file));
      const rejectedType = newFiles.filter((file) => !isAllowedType(file));

      setFileTypeError(rejectedType.length > 0);
      setFileSizeError(rejectedFiles.length > 0);

      if (filtered.length === 0) {
        if (onReject) onReject();
        return;
      }

      if (validateFiles) {
        const validationResult = await validateFiles(filtered);
        const message =
          typeof validationResult === "string"
            ? validationResult.trim().length > 0
              ? validationResult
              : null
            : (validationResult ?? null);

        if (message) {
          onValidationResult?.(message);
          return;
        }
        onValidationResult?.(null);
      }

      const existingEntries = currentFiles ?? [];
      const existingFileObjects = existingEntries.filter(
        (item): item is File => item instanceof File,
      );

      const buildNextState = (
        incomingEntries: UploadEntry[],
        incomingFiles: File[],
      ): { nextEntries: UploadEntry[]; uploadPayload: File[] } => {
        if (isSingleFileMode) {
          const replacementEntry =
            incomingEntries[incomingEntries.length - 1] ??
            existingEntries[existingEntries.length - 1];
          const replacementFile =
            incomingFiles[incomingFiles.length - 1] ??
            existingFileObjects[existingFileObjects.length - 1];

          const nextEntries = replacementEntry ? [replacementEntry] : [];
          const uploadPayload = replacementFile ? [replacementFile] : [];
          return { nextEntries, uploadPayload };
        }

        const combinedEntries = [...existingEntries, ...incomingEntries];
        const combinedFiles = [...existingFileObjects, ...incomingFiles];

        const nextEntries =
          combinedEntries.length > maxFilesLimit
            ? combinedEntries.slice(combinedEntries.length - maxFilesLimit)
            : combinedEntries;
        const uploadPayload =
          combinedFiles.length > maxFilesLimit
            ? combinedFiles.slice(combinedFiles.length - maxFilesLimit)
            : combinedFiles;

        return { nextEntries, uploadPayload };
      };

      const { nextEntries, uploadPayload } = buildNextState(filtered, filtered);
      setFiles(nextEntries);
      syncFilesToStore(filtered, nextEntries);
      if (onUpload) onUpload(uploadPayload);
    },
    [
      currentFiles,
      isAllowedType,
      isUnderMaxSize,
      maxFilesLimit,
      onReject,
      onUpload,
      setFiles,
      isSingleFileMode,
      syncFilesToStore,
    ],
  );

  useEffect(() => {
    if ((!name && !uploadStoreKey) || currentFiles.length) return;
    if (storedFiles.length) {
      setFiles(storedFiles);
    }
  }, [currentFiles.length, name, setFiles, storedFiles, uploadStoreKey]);

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      const selectedCount = acceptedFiles.length + fileRejections.length;
      const dropError = validateDrop?.(selectedCount);
      if (dropError) {
        onValidationResult?.(dropError);
        return;
      }

      await processFiles(acceptedFiles);
    },
    [processFiles, validateDrop, onValidationResult],
  );

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
    multiple: maxFilesLimit > 1,
    maxFiles: maxFilesLimit,
    accept: conditions?.accept,
    disabled: isDisabled,
  });

  const displayErrors = useMemo(() => !conditions?.hideErrors, [conditions?.hideErrors]);
  const showErrorState =
    !isSideDrawer && (fileSizeError || fileTypeError || isError || !!errorMessage);

  const displayedError =
    errorMessage ||
    ((fileTypeError || fileSizeError) &&
      displayErrors &&
      t("form-validation.file-type", { types: allowedExtensionsDisplay }));

  const fileTypeErrorTestId = buildTestId(testId, "file-size-or-type-error");

  return (
    <div className="flex flex-col gap-2 w-full">
      <FormLabel
        data-testid={labelId}
        className="text-primary"
        sx={(theme) => ({
          color: theme.palette.text.primary,
          fontWeight: theme.typography.fontWeightMedium,
        })}
      >
        {label}
        {required ? " *" : ""}
      </FormLabel>
      <FileUploadItem
        testId={testId}
        open={open}
        isDragActive={isDragActive}
        getInputProps={getInputProps}
        getRootProps={getRootProps}
        uploadDescription={uploadDescription}
        disabled={isDisabled}
        hasError={showErrorState}
      />
      {displayedError && (
        <div data-testid={fileTypeErrorTestId}>
          <FormHelperText
            sx={{
              ml: 0,
              fontWeight: 500,
              fontSize: 12,
              marginY: "4px",
              color: "error.main",
            }}
          >
            {displayedError}
          </FormHelperText>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
export { FileUpload };
export const DocumentUpload = FileUpload;
export const CertificateUpload = FileUpload;
